import { useCallback, useEffect, useRef, useState } from "react";
import {
  BuildingOffice2Icon,
  ChevronDownIcon,
  ChevronRightIcon,
  HomeModernIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  createDrawingFloor,
  createDrawingHouse,
  deleteDrawingFloor,
  deleteDrawingHouse,
  fetchDrawingHouses,
} from "./drawingStructureApi";
import {
  readDrawingStructureState,
  writeDrawingStructureState,
} from "./drawingStructureState";
import { DRAWING_KINDS, drawingKindLabel } from "./drawingKinds";
import { drawingKindStyle } from "./drawingKindStyles";

const toStoredIds = (value) => (Array.isArray(value) ? value : []);

export const DrawingStructureTree = ({
  t,
  projectId,
  selectedFloorId,
  selectedHouseId,
  selectedAndre = false,
  selectedAll = false,
  onSelectFloor,
  onSelectHouse,
  onSelectAndre,
  onSelectAll,
  onStructureChange,
  compact = false,
  showAndre = true,
  /** "default" = editor/full CRUD card · "nav" = list sidebar redesign */
  variant = "default",
  totalCount = null,
  unassignedCount = null,
  kindsByFloorId = {},
  showLegend = false,
  manageMode = false,
}) => {
  const initialStructureStateRef = useRef(readDrawingStructureState(projectId));
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedHouseIds, setExpandedHouseIds] = useState(() =>
    toStoredIds(initialStructureStateRef.current.expandedHouseIds),
  );
  const [newHouseName, setNewHouseName] = useState("");
  const [addingHouse, setAddingHouse] = useState(false);
  const [newFloorNames, setNewFloorNames] = useState({});
  const [addingFloorForHouseId, setAddingFloorForHouseId] = useState(null);
  const housesRef = useRef([]);
  const isNav = variant === "nav";

  useEffect(() => {
    housesRef.current = houses;
  }, [houses]);

  const loadStructure = useCallback(
    async (opts = {}) => {
      const { silent = false, initial = false } = opts;
      const showGlobalLoading =
        !silent && (initial || housesRef.current.length === 0);

      try {
        if (showGlobalLoading) {
          setLoading(true);
        }
        const data = await fetchDrawingHouses(projectId);
        setHouses(data);
        onStructureChange?.(data);

        if (initial) {
          const stored = readDrawingStructureState(projectId);
          const storedExpanded = toStoredIds(stored.expandedHouseIds);
          if (storedExpanded.length > 0) {
            setExpandedHouseIds(storedExpanded);
          } else if (data[0]?.id) {
            setExpandedHouseIds([data[0].id]);
          }
        }
      } catch (err) {
        console.error("Error loading drawing structure:", err);
        toast.error(t("drawings.structure.load_error"));
      } finally {
        if (showGlobalLoading) {
          setLoading(false);
        }
      }
    },
    [projectId, onStructureChange, t],
  );

  useEffect(() => {
    initialStructureStateRef.current = readDrawingStructureState(projectId);
    setExpandedHouseIds(
      toStoredIds(initialStructureStateRef.current.expandedHouseIds),
    );
    loadStructure({ initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when project changes
  }, [projectId]);

  useEffect(() => {
    if (!selectedFloorId || houses.length === 0) return;
    const match = houses.find((house) =>
      (house.floors || []).some((floor) => floor.id === selectedFloorId),
    );
    if (!match?.id) return;
    setExpandedHouseIds((current) =>
      current.includes(match.id) ? current : [...current, match.id],
    );
  }, [selectedFloorId, houses]);

  useEffect(() => {
    writeDrawingStructureState(projectId, { expandedHouseIds });
  }, [projectId, expandedHouseIds]);

  const toggleHouse = (houseId) => {
    setExpandedHouseIds((current) =>
      current.includes(houseId)
        ? current.filter((id) => id !== houseId)
        : [...current, houseId],
    );
  };

  const handleAddHouse = async () => {
    const name = newHouseName.trim();
    if (!name) return;

    try {
      const created = await createDrawingHouse({ name, projectId });
      setNewHouseName("");
      setAddingHouse(false);
      setHouses((current) => {
        const next = [...current, { ...created, floors: created.floors || [] }];
        onStructureChange?.(next);
        return next;
      });
      toast.success(t("drawings.structure.house_created"));
    } catch (err) {
      console.error("Error creating drawing house:", err);
      toast.error(t("drawings.structure.save_error"));
    }
  };

  const handleAddFloor = async (houseId) => {
    const name = (newFloorNames[houseId] || "").trim();
    if (!name) return;

    try {
      const created = await createDrawingFloor(houseId, name);
      setNewFloorNames((current) => ({ ...current, [houseId]: "" }));
      setAddingFloorForHouseId(null);
      setExpandedHouseIds((current) => [...new Set([...current, houseId])]);
      setHouses((current) =>
        current.map((house) =>
          house.id === houseId
            ? {
                ...house,
                floors: [
                  ...(house.floors || []),
                  { ...created, drawings_count: 0 },
                ],
              }
            : house,
        ),
      );
      toast.success(t("drawings.structure.floor_created"));
    } catch (err) {
      console.error("Error creating drawing floor:", err);
      toast.error(t("drawings.structure.save_error"));
    }
  };

  const handleDeleteHouse = async (house) => {
    if (!window.confirm(t("drawings.structure.delete_house_confirm"))) return;

    try {
      await deleteDrawingHouse(house.id);
      if (
        selectedFloorId &&
        (house.floors || []).some((f) => f.id === selectedFloorId)
      ) {
        onSelectFloor?.(null);
      }
      if (selectedHouseId === house.id) {
        onSelectHouse?.(null);
      }
      setHouses((current) => {
        const next = current.filter((item) => item.id !== house.id);
        onStructureChange?.(next);
        return next;
      });
      setExpandedHouseIds((current) => current.filter((id) => id !== house.id));
      toast.success(t("drawings.structure.house_deleted"));
    } catch (err) {
      console.error("Error deleting drawing house:", err);
      toast.error(t("drawings.structure.delete_error"));
    }
  };

  const handleDeleteFloor = async (floor) => {
    if (!window.confirm(t("drawings.structure.delete_floor_confirm"))) return;

    try {
      await deleteDrawingFloor(floor.id);
      if (selectedFloorId === floor.id) {
        onSelectFloor?.(null);
      }
      setHouses((current) => {
        const next = current.map((house) => ({
          ...house,
          floors: (house.floors || []).filter((item) => item.id !== floor.id),
        }));
        onStructureChange?.(next);
        return next;
      });
      toast.success(t("drawings.structure.floor_deleted"));
    } catch (err) {
      console.error("Error deleting drawing floor:", err);
      toast.error(t("drawings.structure.delete_error"));
    }
  };

  const houseCount = (house) =>
    (house.floors || []).reduce(
      (sum, floor) => sum + (floor.drawings_count || 0),
      0,
    );

  if (isNav) {
    return (
      <div className="space-y-1">
        <div className="mb-2 flex items-center justify-between px-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#79828F]">
            {t("drawings.structure.title")}
          </div>
          <button
            type="button"
            onClick={() => setAddingHouse((v) => !v)}
            className="text-[12px] text-[#1F4BE0] hover:text-[#163AB4]"
          >
            + {t("drawings.house")}
          </button>
        </div>

        {addingHouse || manageMode ? (
          <div className="mb-2 flex gap-1.5">
            <input
              type="text"
              value={newHouseName}
              onChange={(event) => setNewHouseName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddHouse();
                }
              }}
              placeholder={t("drawings.structure.new_house_placeholder_short")}
              className="min-w-0 flex-1 rounded-md border border-[#C9D6FA] px-2.5 py-1.5 text-[13px] outline-none"
            />
            <button
              type="button"
              onClick={handleAddHouse}
              className="rounded-md bg-[#1F4BE0] px-2.5 py-1.5 text-[12px] font-medium text-white"
            >
              {t("drawings.list_ui.add")}
            </button>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => onSelectAll?.()}
          className={`relative flex w-full items-center gap-2.5 overflow-hidden rounded-[7px] px-2.5 py-2 text-left ${
            selectedAll ? "" : "hover:bg-[#EDEFF3]"
          }`}
        >
          {selectedAll ? (
            <span className="absolute inset-0 bg-[#E6ECFF]" />
          ) : null}
          <span className="relative h-[13px] w-[13px] rounded-[3px] border-[1.5px] border-[#1F4BE0]" />
          <span className="relative flex-1 text-[13px] font-semibold text-[#12161C]">
            {t("drawings.list_ui.all_drawings")}
          </span>
          {totalCount != null ? (
            <span className="relative font-mono text-[12px] text-[#6B7482]">
              {totalCount}
            </span>
          ) : null}
        </button>

        {loading ? (
          <p className="px-2 py-3 text-[13px] text-[#6B7482]">
            {t("Loading...")}
          </p>
        ) : houses.length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-[#D6DAE0] px-3 py-3 text-[12px] text-[#6B7482]">
            {t("drawings.structure.empty")}
          </p>
        ) : (
          houses.map((house) => {
            const expanded = expandedHouseIds.includes(house.id);
            return (
              <div key={house.id} className="mt-1.5">
                <div className="flex items-center gap-1 rounded-[7px] hover:bg-[#EDEFF3]">
                  <button
                    type="button"
                    onClick={() => toggleHouse(house.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5 text-left"
                  >
                    <span className="w-2 text-[10px] text-[#79828F]">
                      {expanded ? "▾" : "▸"}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[#12161C]">
                      {house.name}
                    </span>
                    <span className="font-mono text-[12px] text-[#6B7482]">
                      {houseCount(house)}
                    </span>
                  </button>
                  {manageMode ? (
                    <button
                      type="button"
                      onClick={() => handleDeleteHouse(house)}
                      className="mr-1 grid h-7 w-7 place-items-center rounded-md text-[#969da8] hover:bg-red-50 hover:text-red-600"
                      title={t("drawings.structure.delete_house")}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>

                {expanded ? (
                  <div className="ml-3.5 flex flex-col gap-px border-l border-[#E1E4E9] pl-4">
                    {(house.floors || []).map((floor) => {
                      const active = selectedFloorId === floor.id;
                      const kinds = kindsByFloorId[floor.id] || [];
                      return (
                        <div
                          key={floor.id}
                          className="relative flex items-center overflow-hidden rounded-md"
                        >
                          {active ? (
                            <span className="absolute inset-0 bg-[#E6ECFF]" />
                          ) : null}
                          <button
                            type="button"
                            onClick={() => {
                              onSelectHouse?.(null);
                              onSelectFloor?.(active ? null : floor.id);
                            }}
                            className="relative flex min-w-0 flex-1 items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[#EDEFF3]"
                          >
                            <span className="min-w-0 flex-1 truncate text-[13px] text-[#12161C]">
                              {floor.name}
                            </span>
                            <span className="flex gap-[3px]">
                              {kinds.map((kind) => {
                                const style = drawingKindStyle(kind);
                                return (
                                  <span
                                    key={kind}
                                    className="h-1.5 w-1.5 rounded-full"
                                    style={{ background: style.color }}
                                    title={drawingKindLabel(t, kind)}
                                  />
                                );
                              })}
                            </span>
                            <span className="w-3.5 text-right font-mono text-[12px] text-[#6B7482]">
                              {floor.drawings_count || 0}
                            </span>
                          </button>
                          {manageMode ? (
                            <button
                              type="button"
                              onClick={() => handleDeleteFloor(floor)}
                              className="relative mr-0.5 grid h-7 w-7 place-items-center rounded-md text-[#969da8] hover:bg-red-50 hover:text-red-600"
                              title={t("drawings.structure.delete_floor")}
                            >
                              <TrashIcon className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}

                    {addingFloorForHouseId === house.id || manageMode ? (
                      <div className="mt-1 flex gap-1 px-1 pb-1">
                        <input
                          type="text"
                          value={newFloorNames[house.id] || ""}
                          onChange={(event) =>
                            setNewFloorNames((current) => ({
                              ...current,
                              [house.id]: event.target.value,
                            }))
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              event.preventDefault();
                              handleAddFloor(house.id);
                            }
                          }}
                          placeholder={t(
                            "drawings.structure.new_floor_placeholder_short",
                          )}
                          className="min-w-0 flex-1 rounded-md border border-[#C9D6FA] px-2 py-1 text-[12px] outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleAddFloor(house.id)}
                          className="rounded-md bg-[#1F4BE0] px-2 py-1 text-[11px] font-medium text-white"
                        >
                          {t("drawings.list_ui.add")}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setAddingFloorForHouseId(house.id)}
                        className="px-2.5 py-1.5 text-left text-[12px] text-[#79828F] hover:text-[#1F4BE0]"
                      >
                        + {t("drawings.floor")}
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })
        )}

        {showAndre ? (
          <button
            type="button"
            onClick={() => onSelectAndre?.()}
            className={`relative mt-2 flex w-full items-center gap-2.5 overflow-hidden rounded-[7px] px-2.5 py-2 text-left ${
              selectedAndre ? "" : "hover:bg-[#EDEFF3]"
            }`}
          >
            {selectedAndre ? (
              <span className="absolute inset-0 bg-[#E6ECFF]" />
            ) : null}
            <span className="relative flex-1 text-[13px] text-[#37414F]">
              {t("drawings.structure.andre")}{" "}
              <span className="text-[#9AA1AC]">
                · {t("drawings.structure.andre_hint_short").toLowerCase()}
              </span>
            </span>
            {unassignedCount != null ? (
              <span className="relative font-mono text-[12px] text-[#6B7482]">
                {unassignedCount}
              </span>
            ) : null}
          </button>
        ) : null}

        {showLegend ? (
          <div className="mt-5 rounded-[9px] border border-[#E4E7EC] bg-white p-3">
            <div className="text-[12px] font-semibold text-[#12161C]">
              {t("drawings.list_ui.legend")}
            </div>
            <div className="mt-2.5 flex flex-col gap-1.5">
              {DRAWING_KINDS.map((kind) => {
                const style = drawingKindStyle(kind);
                return (
                  <div key={kind} className="flex items-center gap-2">
                    <span
                      className="h-[7px] w-[7px] rounded-full"
                      style={{ background: style.color }}
                    />
                    <span className="text-[12px] text-[#4B5563]">
                      {drawingKindLabel(t, kind)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
          {t("drawings.structure.title")}
        </div>
        {!compact && (
          <p className="mt-1 text-[12px] leading-relaxed text-[#59616d]">
            {t("drawings.structure.help")}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={newHouseName}
          onChange={(event) => setNewHouseName(event.target.value)}
          placeholder={t("drawings.structure.new_house_placeholder")}
          className="min-w-0 flex-1 rounded-lg border border-[#e5e8ec] px-3 py-2 text-[13px] outline-none focus:border-[#2b6ef0]"
        />
        <button
          type="button"
          onClick={handleAddHouse}
          className="inline-flex items-center gap-1 rounded-lg bg-[#2b6ef0] px-3 py-2 text-[12px] font-semibold text-white"
        >
          <PlusIcon className="h-4 w-4" />
          {t("drawings.structure.add_house")}
        </button>
      </div>

      {showAndre ? (
        <button
          type="button"
          onClick={() => {
            onSelectAndre?.();
          }}
          className={`flex w-full items-center gap-2 rounded-[10px] border px-3 py-2.5 text-left ${
            selectedAndre
              ? "border-[#2b6ef0] bg-[#eaf1fe]"
              : "border-[#e5e8ec] bg-white hover:bg-[#f6f7f9]"
          }`}
        >
          <span
            className={`text-[13px] font-semibold ${
              selectedAndre ? "text-[#1d4fb0]" : "text-[#14171c]"
            }`}
          >
            {t("drawings.structure.andre")}
          </span>
          <span className="ml-auto text-[11px] text-[#969da8]">
            {t("drawings.structure.andre_hint_short")}
          </span>
        </button>
      ) : null}

      {loading ? (
        <p className="text-[13px] text-[#59616d]">{t("Loading...")}</p>
      ) : houses.length === 0 ? (
        <p className="rounded-lg border border-dashed border-[#d6dae0] px-3 py-4 text-[13px] text-[#59616d]">
          {t("drawings.structure.empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {houses.map((house) => {
            const expanded = expandedHouseIds.includes(house.id);
            const houseActive =
              selectedHouseId === house.id && !selectedFloorId;
            return (
              <li
                key={house.id}
                className="overflow-hidden rounded-[10px] border border-[#e5e8ec] bg-[#f6f7f9]"
              >
                <div className="flex items-center gap-1 px-2 py-2">
                  <button
                    type="button"
                    onClick={() => toggleHouse(house.id)}
                    className="grid h-7 w-7 place-items-center rounded-md text-[#59616d] hover:bg-white"
                  >
                    {expanded ? (
                      <ChevronDownIcon className="h-4 w-4" />
                    ) : (
                      <ChevronRightIcon className="h-4 w-4" />
                    )}
                  </button>
                  <BuildingOffice2Icon className="h-4 w-4 text-[#2b6ef0]" />
                  <button
                    type="button"
                    onClick={() => {
                      onSelectHouse?.(houseActive ? null : house.id);
                      if (!houseActive) onSelectFloor?.(null);
                    }}
                    className={`min-w-0 flex-1 truncate text-left text-[13px] font-semibold ${
                      houseActive ? "text-[#1d4fb0]" : "text-[#14171c]"
                    }`}
                  >
                    {house.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteHouse(house)}
                    className="grid h-7 w-7 place-items-center rounded-md text-[#969da8] hover:bg-red-50 hover:text-red-600"
                    title={t("drawings.structure.delete_house")}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                {expanded && (
                  <div className="border-t border-[#e5e8ec] bg-white px-2 py-2">
                    <ul className="space-y-1">
                      {(house.floors || []).map((floor) => {
                        const active = selectedFloorId === floor.id;
                        return (
                          <li key={floor.id}>
                            <div
                              className={`flex items-center gap-1 rounded-lg ${
                                active ? "bg-[#eaf1fe]" : "hover:bg-[#f6f7f9]"
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  onSelectHouse?.(null);
                                  onSelectFloor?.(active ? null : floor.id);
                                }}
                                className="flex min-w-0 flex-1 items-center gap-2 px-2 py-2 text-left"
                              >
                                <HomeModernIcon className="h-4 w-4 text-[#59616d]" />
                                <span className="truncate text-[13px] font-medium text-[#14171c]">
                                  {t("drawings.floor")} {floor.name}
                                </span>
                                <span className="ml-auto font-mono text-[10px] text-[#969da8]">
                                  {floor.drawings_count || 0}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteFloor(floor)}
                                className="grid h-7 w-7 place-items-center rounded-md text-[#969da8] hover:bg-red-50 hover:text-red-600"
                                title={t("drawings.structure.delete_floor")}
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>

                    <div className="mt-2 flex gap-2">
                      <input
                        type="text"
                        value={newFloorNames[house.id] || ""}
                        onChange={(event) =>
                          setNewFloorNames((current) => ({
                            ...current,
                            [house.id]: event.target.value,
                          }))
                        }
                        placeholder={t(
                          "drawings.structure.new_floor_placeholder",
                        )}
                        className="min-w-0 flex-1 rounded-lg border border-[#e5e8ec] px-3 py-2 text-[12px] outline-none focus:border-[#2b6ef0]"
                      />
                      <button
                        type="button"
                        onClick={() => handleAddFloor(house.id)}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#e5e8ec] bg-white px-2.5 py-2 text-[11px] font-semibold text-[#14171c] hover:bg-[#f6f7f9]"
                      >
                        <PlusIcon className="h-3.5 w-3.5" />
                        {t("drawings.structure.add_floor")}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
